import { useContext } from 'react'

import type { UseFormOptions, UseFormPayload } from '@domain-group/react-hooks'
import {
  useForm, useLatestEntity,
  useThunkDispatch,
} from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import { mapAgencyToState } from '@functions/agency'
import { baseURL } from '@functions/api'
import { loginAsEntityUser, mergeAndStringifyEntityObjects } from '@functions/entity'

import useSelector from '@hooks/useSelector'

import * as agencyActions from '@redux/modules/agency'
import type { EntityOptions } from '@redux/modules/module'
import type { AppDispatch } from '@redux/store'

import defaultState, { requiredFields } from '@models/agency'
import type {
  AgencyModel, BrandModel, PricingPlanModel, ProviderAccountModel, UserModel,
} from '@models/types'

import PageContext from '@contexts/pageContext'

const stringifyObjectKeys = ['data', 'features', 'options']

type GenerateUrlsOptions = {
  prefixRoute?: string,
}

type UseAgencyOptions = {
  generateUrlsOptions?: GenerateUrlsOptions,
}

export const generateUrls = (agency?: Partial<AgencyModel>, options?: GenerateUrlsOptions) => {
  const { id } = agency || {}
  const { prefixRoute = 'systemManager' } = options || {}
  const isSystemManager = prefixRoute === 'systemManager'

  return {
    agenciesIndexUrl: `#/${prefixRoute}/agencies`,
    agencySalesforceDetailsUrl: `#/${prefixRoute}/agencies/${id}/edit/salesforceDetails`,
    editAgencyIntegrationsUrl: `#/${prefixRoute}/agencies/${id}/edit/integrations`,
    editAgencyRelationsUrl: `#/${prefixRoute}/agencies/${id}/edit/relations`,
    editAgencyUrl: isSystemManager ? `#/${prefixRoute}/agencies/${id}/edit` : `#/${prefixRoute}/details`,
    editProviderAccountUrl: `#/${prefixRoute}/providerAgencies/${id}/edit`,
    exportAgencyApiKeys: `${baseURL}/api_keys/export_agency_api_keys_csv.json?agency_id=${agency?.id}`,
  }
}

type CreateAgencyPayload = {
  agencyParams: AgencyModel,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const createAgency = (params: CreateAgencyPayload) => {
  const { agencyParams, dispatch, requestOptions } = params
  const { createAgency: createFn } = agencyActions

  // @ts-expect-error TS2345
  const updatedParams = mergeAndStringifyEntityObjects({}, agencyParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response

      const { editAgencyUrl } = generateUrls(data)

      return {
        success,
        data,
        redirectUrl: editAgencyUrl,
      }
    }

    return response
  })
}

export type CopyAgencyOptions = {
  destinationAgencyId: number,
  selectedEntityKeys: string[],
  sourceAgencyId: number,
}

type CopyAgencyParams = CopyAgencyOptions & {
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

export const copyAgency = (params: CopyAgencyParams) => {
  const {
    destinationAgencyId, dispatch, requestOptions, selectedEntityKeys, sourceAgencyId,
  } = params
  const { copyAgencyData: copyFn } = agencyActions

  return dispatch(copyFn({ destinationAgencyId, sourceAgencyId, selectedEntityKeys }, requestOptions))
}

type ExportAgencyPayload = {
  agency: AgencyModel,
  dispatch: AppDispatch,
}

export const exportAgency = (params: ExportAgencyPayload) => {
  const { agency, dispatch } = params
  const { exportAgency: exportFn } = agencyActions

  return dispatch(exportFn(agency))
}

type UpdateAgencyPayload = {
  agency: AgencyModel,
  agencyParams: Partial<AgencyModel>,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

export const updateAgency = (params: UpdateAgencyPayload) => {
  const {
    agency, agencyParams, dispatch, requestOptions,
  } = params
  const { updateAgency: updateFn } = agencyActions

  // @ts-expect-error TS2345
  const updatedParams = mergeAndStringifyEntityObjects(agency, agencyParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

export type WatermarkImageParams = {
  filename: string,
  watermark_image_file: string,
}

type UpdateWatermarkImageParams = {
  agency: AgencyModel,
  watermarkImage: WatermarkImageParams,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

export const updateWatermarkImage = (params: UpdateWatermarkImageParams) => {
  const {
    agency, watermarkImage, dispatch, requestOptions,
  } = params
  const { updateWatermarkImage: updateWatermarkImageFn } = agencyActions

  return dispatch(updateWatermarkImageFn(agency, watermarkImage, requestOptions))
}

type LoginAsAgencyPayload = {
  agency: AgencyModel,
  dispatch: AppDispatch,
  requestOptions: EntityOptions,
}

const loginAsAgency = (params: LoginAsAgencyPayload) => {
  const { agency, dispatch, requestOptions } = params

  return dispatch(loginAsEntityUser('agencies', agency.id, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response

      return {
        success,
        // @ts-expect-error TS2339
        redirectUrl: data?.url,
      }
    }

    return response
  })
}

type UpdateTemplatePermissionsPayload = {
  agencyId: number,
  dispatch: AppDispatch,
  permissionAction: 'assign' | 'unassign',
  serviceId: number,
}

const updateTemplatePermissions = (params: UpdateTemplatePermissionsPayload) => {
  const {
    agencyId, permissionAction, serviceId, dispatch,
  } = params

  const { updateTemplatePermissions: updateFn } = agencyActions

  return dispatch(updateFn(agencyId, permissionAction, serviceId))
}

const toggleAgencyLock = (params: { agency: AgencyModel, dispatch: AppDispatch }) => {
  const { agency, dispatch } = params
  const { toggleAgencyLock: toggleFn } = agencyActions

  const requestOptions = { include_locked_by_user: true }

  return dispatch(toggleFn(agency.id, requestOptions))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useAgencyForm(
  agency: Partial<AgencyModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
): UseFormPayload {
  const { customRequiredFields = [], onBlurFn, validateOn } = options || {}

  const agencyForm = useForm(
    defaultState,
    {
      // @ts-expect-error TS2345
      entity: mapAgencyToState(agency),
      onBlurFn,
      requiredFields: [...requiredFields, ...customRequiredFields],
      validateOn,
    },
    [agency.id, agency.cache_key],
  )

  return {
    ...agencyForm,
  }
}

export type UseAgencyFormPayload = ReturnType<typeof useAgencyForm>

export type UseRelationsPayload = {
  brand: Partial<BrandModel>,
  lockedByUser: Partial<UserModel>,
  pricingPlan: Partial<PricingPlanModel>,
  providerAccount: Partial<ProviderAccountModel>,
}

export function useRelations(agency: Partial<AgencyModel>): UseRelationsPayload {
  const {
    brand_id, locked_by_user_id, pricing_plan_id, provider_account_id,
  } = agency || {}

  const entities = useSelector(reduxState => reduxState.entities)
  const {
    brands, pricingPlans, providerAccounts, users,
  } = entities

  const brand: Partial<BrandModel> = brand_id ? brands[brand_id] || {} : {}
  const lockedByUser: Partial<UserModel> = locked_by_user_id ? users[locked_by_user_id] || {} : {}
  const pricingPlan: Partial<PricingPlanModel> = pricing_plan_id ? pricingPlans[pricing_plan_id] || {} : {}
  const providerAccount: Partial<ProviderAccountModel> = provider_account_id
    ? providerAccounts[provider_account_id] || {} : {}

  return {
    brand,
    lockedByUser,
    pricingPlan,
    providerAccount,
  }
}

function useAgency(initEntity: Partial<AgencyModel> = {}, options: UseAgencyOptions = {}) {
  const { generateUrlsOptions } = options

  const { entity: agency } = useLatestEntity(initEntity, 'agencies')

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)

  const { creating, loading, updating } = useSelector(reduxState => reduxState.agencies)

  const {
    brand, lockedByUser, pricingPlan, providerAccount,
  } = useRelations(agency)

  const supportUserIds = agency?.data?.support_team?.user_ids || []
  const agencySalesforceDetails = agency?.salesforce_details ?? {}

  const brandControlledImageSettingsEnabled = brand?.options?.brand_controlled_image_settings_enabled || false
  const brandImageSettingsExcludedAgencyIds = brand?.options?.brand_image_settings_excluded_agency_ids || []

  const hasBrandImageSettings = brandControlledImageSettingsEnabled
    // @ts-expect-error TS2339
    && brandImageSettingsExcludedAgencyIds.includes(agency?.id)

  return {
    agency,
    brand,
    callbacks: {
      copyAgencyData: () => {
        launchModal({
          callbacks,
          modalKey: 'CopyAgencyDataModal',
          payload: { agency },
        })
      },
      createAgency: (
        agencyParams: AgencyModel,
        requestOptions: EntityOptions,
      ) => createAgency({ agencyParams, dispatch, requestOptions }),
      copyAgency: (copyParams: CopyAgencyOptions, requestOptions?: EntityOptions) => copyAgency(
        { ...copyParams, dispatch, requestOptions },
      ),
      editTaskPricing: (customPayload: object) => launchModal({
        callbacks,
        modalKey: 'EditTaskPricingModal',
        payload: { agency, ...customPayload },
      }),
      // @ts-expect-error TS2322
      exportAgency: () => exportAgency({ agency, dispatch }),
      // @ts-expect-error TS2322
      loginAsAgency: (requestOptions: EntityOptions) => loginAsAgency({ agency, dispatch, requestOptions }),
      manageAgencyOptions: () => launchModal({
        callbacks,
        modalKey: 'ManageAgencyOptionsModal',
        payload: { agency },
      }),
      manageAgencyFeatures: () => launchModal({
        callbacks,
        modalKey: 'ManageAgencyFeaturesModal',
        payload: { agency },
      }),
      manageAgencyEnabledStatus: () => launchModal({
        callbacks,
        modalKey: 'ManageAgencyEnabledStatusModal',
        payload: { agency },
      }),
      manageAgencySandboxStatus: () => launchModal({
        callbacks,
        modalKey: 'ManageAgencySandboxStatusModal',
        payload: { agency },
      }),
      manageAgencyImages: (customPayload: {
        image_type: string,
        imageable_id: number,
        imageable_type: string,
      }) => launchModal({
        callbacks,
        modalKey: 'ManageEntityImagesModal',
        payload: { imageOptions: { ...customPayload } },
      }),
      manageAgencyPricingPlan: () => launchModal({
        callbacks,
        modalKey: 'ManageAgencyPricingPlanModal',
        payload: { agency },
      }),
      manageAgencySubscription: () => launchModal({
        callbacks,
        modalKey: 'ManageAgencySubscriptionModal',
        payload: { agency },
      }),
      manageAgencyIntegrationPermissions: () => launchModal({
        callbacks,
        modalKey: 'ManageAgencyIntegrationPermissionsModal',
        payload: { agency },
      }),
      manageAgencySupportTeam: () => launchModal({
        callbacks,
        modalKey: 'ManageEntitySupportTeamModal',
        payload: {
          entity: agency,
          callbacks: {
            updateEntity: (agencyParams: AgencyModel, requestOptions: EntityOptions) => updateAgency({
              // @ts-expect-error TS2322
              agency, agencyParams, dispatch, requestOptions,
            }),
          },
        },
      }),
      manageAgencyTemplates: () => launchModal({
        callbacks,
        modalKey: 'ManageAgencyTemplatesModal',
        payload: { agency },
      }),
      // @ts-expect-error TS2322
      toggleAgencyLock: () => toggleAgencyLock({ agency, dispatch }),
      updateAgency: (agencyParams: Partial<AgencyModel>, requestOptions?: EntityOptions) => updateAgency({
        // @ts-expect-error TS2322
        agency, agencyParams, dispatch, requestOptions,
      }),
      updateWatermarkImage: (
        watermarkImage: WatermarkImageParams,
        requestOptions?: EntityOptions,
      ) => updateWatermarkImage({
        // @ts-expect-error TS2322
        agency, watermarkImage, dispatch, requestOptions,
      }),
      updateTemplatePermissions: (
        permissionAction: 'assign' | 'unassign',
        serviceId: number,
      ) => updateTemplatePermissions({
        // @ts-expect-error TS2339
        agencyId: agency.id,
        permissionAction,
        serviceId,
        dispatch,
      }),
      selectIntegrationPlatform: () => launchModal({
        callbacks,
        modalKey: 'SelectIntegrationPlatformModal',
        payload: { agency },
      }),
    },
    creating: !!creating,
    hasBrandImageSettings,
    isActive: agency.active,
    loading: !!loading,
    lockedByUser,
    pricingPlan,
    providerAccount,
    supportUserIds,
    agencySalesforceDetails,
    updating: !!updating,
    urls: generateUrls(agency, generateUrlsOptions),
  }
}

export type UseAgencyPayload = ReturnType<typeof useAgency>

export default useAgency
